import React, { useEffect, useState } from 'react';
import {
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  Tooltip,
  IconButton,
} from '@mui/material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import DialogGlynt from '../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../common/Dialog/DialogActionsGlynt';
import mapTokenizerVersion from '../../utils/mapTokenizerVersion';
import errorResponses from '../../errorResponses';
import useGlynt from '../hooks/useGlynt';
import useOCREngines from '../hooks/useOCREngines';
import { AppContext } from '../../App';
import { AxiosError } from 'axios';
import {
  IDataPool,
  DataPoolStatusType,
  DataPoolUseType,
  DataPoolPriorityType,
  ValidationDataset,
} from '../../types/my-types';
import { useGetRouterConfig } from '../hooks/useGetRouterConfig';
import useClients from '../hooks/useClients';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export interface GlyntTagsEditDialogProps {
  dataPool: IDataPool;
  dataPoolNames: string[];
  open?: boolean;
  onDialogClose?: () => void;
}

function GlyntTagsEditDialog({
  dataPool,
  dataPoolNames,
  open: controlledOpen,
  onDialogClose,
}: GlyntTagsEditDialogProps) {
  const { setToast, isStaff, orgId } = React.useContext(AppContext);
  const glyntTags = dataPool.glynt_tags.join(', ');

  const isControlled = controlledOpen !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const open = isControlled ? controlledOpen : internalOpen;

  const setOpen = (value: boolean) => {
    if (!isControlled) setInternalOpen(value);
    if (!value) onDialogClose?.();
  };

  const [labelValue, setLabelValue] = useState(dataPool.label);
  const [labelError, setLabelError] = useState('');
  const [transformerValue, setValue] = useState(glyntTags);
  const [transformerError, setTransformerError] = useState('');

  const [routerPrefixError, setRouterPrefixError] = useState('');
  const [routerPrefixValue, setRouterPrefixValue] = useState('');
  const [priority, setPriority] = useState<DataPoolPriorityType>(dataPool.priority ?? 'Normal');
  const [use, setUse] = useState(dataPool.use);
  const [status, setStatus] = useState(dataPool.status);
  const [usageDecimalPlaces, setUsageDecimalPlaces] = useState(dataPool.usage_decimal_places ?? 2);
  const [suggestNewVendors, setSuggestNewVendors] = useState(
    dataPool.config?.suggest_new_vendors ?? false
  );
  const [validationDatasets, setValidationDatasets] = useState<ValidationDataset[]>(
    dataPool.config?.validation_datasets ?? []
  );
  const [dataRelevantFrom, setDataRelevantFrom] = useState<Dayjs | null>(
    dataPool.config?.data_relevant_from ? dayjs(dataPool.config.data_relevant_from) : null
  );

  const [version, setVersion] = useState<string | undefined>(
    dataPool.default_tokenizer_version.name
  );
  const [clientValue, setClientValue] = useState<number | null>(dataPool.client?.id ?? null);

  const { data, isLoading, isError } = useOCREngines(open);
  const { data: clientsList } = useClients(orgId, open);
  const {
    data: routeConfigData,
    isLoading: isLoadingRouteConfig,
    isFetching: isFetchingRouteConfig,
    isSuccess: isSuccessRouteConfig,
  } = useGetRouterConfig(dataPool.id, open);

  useEffect(() => {
    setSuggestNewVendors(dataPool.config?.suggest_new_vendors ?? false);
  }, [dataPool.config?.suggest_new_vendors]);

  useEffect(() => {
    setValidationDatasets(dataPool.config?.validation_datasets ?? []);
  }, [dataPool.config?.validation_datasets]);

  useEffect(() => {
    setDataRelevantFrom(
      dataPool.config?.data_relevant_from ? dayjs(dataPool.config.data_relevant_from) : null
    );
  }, [dataPool.config?.data_relevant_from]);

  useEffect(() => {
    if (routeConfigData?.route_prefix?.prefix) {
      setRouterPrefixValue(routeConfigData?.route_prefix?.prefix);
    }
  }, [isSuccessRouteConfig, isFetchingRouteConfig, routeConfigData?.route_prefix?.prefix]);

  useEffect(() => {
    if (open) {
      setLabelValue(dataPool.label);
      setLabelError('');
      setValue(glyntTags);
      setTransformerError('');
      setRouterPrefixValue(routeConfigData?.route_prefix?.prefix || '');
      setRouterPrefixError('');
      setPriority(dataPool.priority ?? 'Normal');
      setUse(dataPool.use);
      setStatus(dataPool.status);
      setUsageDecimalPlaces(dataPool.usage_decimal_places ?? 2);
      setSuggestNewVendors(dataPool.config?.suggest_new_vendors ?? false);
      setValidationDatasets(dataPool.config?.validation_datasets ?? []);
      setDataRelevantFrom(
        dataPool.config?.data_relevant_from ? dayjs(dataPool.config.data_relevant_from) : null
      );
      setVersion(dataPool.default_tokenizer_version.name);
      setClientValue(dataPool.client?.id ?? null);
    }
  }, [open]);

  useEffect(() => {
    if (open && isError) {
      setToast({
        variant: 'error',
        open: true,
        message: errorResponses.failedToLoadOCR,
      });
    }
  }, [isError, open, setToast]);

  const glynt = useGlynt();
  const client = useQueryClient();

  const updateRouterPrefix = useMutation({
    mutationFn: async () =>
      (await glynt).dataPool.updateRouterPrefixApi(dataPool.id, routerPrefixValue),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['datapools'] });
      client.invalidateQueries({ queryKey: ['data-pool-route-config'] });
    },
    onError: (error: AxiosError) => {
      const parseError = JSON.parse(error.message);
      const errorMessage = parseError?.error || 'There was an error saving the router prefix';
      setRouterPrefixError(errorMessage);
      setRouterPrefixError(`${errorResponses.failedToSetRouterPrefix} ${errorMessage}`);
    },
  });

  React.useEffect(() => {
    if (transformerValue.length > 99) {
      setTransformerError('Incorrect entry. max length is 100 characters.');
    } else {
      setTransformerError('');
    }
  }, [transformerValue]);

  React.useEffect(() => {
    if (labelValue.trim().length === 0) {
      setLabelError('Name is required.');
    } else if (labelValue !== dataPool.label && dataPoolNames.includes(labelValue)) {
      setLabelError('Name already exists.');
    } else {
      setLabelError('');
    }
  }, [labelValue, dataPool.label, dataPoolNames]);

  const handleSubmit = async () => {
    try {
      if (routerPrefixValue !== routeConfigData?.route_prefix?.prefix) {
        await updateRouterPrefix.mutateAsync();
      }

      if (
        labelValue !== dataPool.label ||
        transformerValue !== glyntTags ||
        version !== dataPool.default_tokenizer_version.name ||
        use !== dataPool.use ||
        status !== dataPool.status ||
        priority !== dataPool.priority ||
        usageDecimalPlaces !== (dataPool.usage_decimal_places ?? 2) ||
        suggestNewVendors !== (dataPool.config?.suggest_new_vendors ?? false) ||
        JSON.stringify(validationDatasets) !==
          JSON.stringify(dataPool.config?.validation_datasets ?? []) ||
        clientValue !== (dataPool.client?.id ?? null) ||
        (dataRelevantFrom?.isValid() ? dataRelevantFrom.format('YYYY-MM-DD') : null) !==
          (dataPool.config?.data_relevant_from ?? null)
      ) {
        await updateDataPool.mutateAsync();
      }

      setOpen(false);
      setToast({
        variant: 'success',
        open: true,
        message: 'Datapool Settings Updated!',
      });
    } catch (err) {
      setToast({
        variant: 'error',
        open: true,
        message: 'There was an error saving the data.',
      });
      console.error(err);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setLabelValue(dataPool.label);
    setLabelError('');
    setRouterPrefixValue(routeConfigData?.route_prefix?.prefix || '');
    setTransformerError('');
    setRouterPrefixError('');
    setPriority(dataPool.priority ?? 'Normal');
    setUse(dataPool.use);
    setStatus(dataPool.status);
    setUsageDecimalPlaces(dataPool.usage_decimal_places ?? 2);
    setSuggestNewVendors(dataPool.config?.suggest_new_vendors ?? false);
    setValidationDatasets(dataPool.config?.validation_datasets ?? []);
    setDataRelevantFrom(
      dataPool.config?.data_relevant_from ? dayjs(dataPool.config.data_relevant_from) : null
    );
    setVersion(dataPool.default_tokenizer_version.name);
    setClientValue(dataPool.client?.id ?? null);
  };

  const handleOnChangeTransformer = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setValue(e.target.value);
    setTransformerError('');
  };

  const handleOnChangeRouterPrefix = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRouterPrefixValue(e.target.value);
    setRouterPrefixError('');
  };

  const updateDataPool = useMutation({
    mutationFn: async () =>
      (await glynt).dataPool.update(dataPool.id, {
        label: labelValue,
        default_tokenizer_version: version === 'latest' ? null : version,
        glynt_tags: transformerValue.length === 0 ? [] : transformerValue.split(', '),
        use,
        status,
        priority,
        usage_decimal_places: usageDecimalPlaces,
        config: {
          ...dataPool.config,
          suggest_new_vendors: suggestNewVendors,
          validation_datasets: validationDatasets,
          data_relevant_from: dataRelevantFrom?.isValid() ? dataRelevantFrom.format('YYYY-MM-DD') : null,
        },
        client: clientValue,
      }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['datapools'] });
      client.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  const isSaving = updateDataPool.isPending || updateRouterPrefix.isPending;

  const isSubmitDisabled =
    (labelValue === dataPool.label &&
      transformerValue === glyntTags &&
      (routerPrefixValue || '') === (routeConfigData?.route_prefix?.prefix || '') &&
      use === dataPool.use &&
      status === dataPool.status &&
      priority === dataPool.priority &&
      usageDecimalPlaces === (dataPool.usage_decimal_places ?? 2) &&
      suggestNewVendors === (dataPool.config?.suggest_new_vendors ?? false) &&
      JSON.stringify(validationDatasets) ===
        JSON.stringify(dataPool.config?.validation_datasets ?? []) &&
      clientValue === (dataPool.client?.id ?? null) &&
      (dataRelevantFrom?.isValid() ? dataRelevantFrom.format('YYYY-MM-DD') : null) ===
        (dataPool.config?.data_relevant_from ?? null) &&
      (version === dataPool.default_tokenizer_version.name || !version)) ||
    labelError.length > 0 ||
    routerPrefixError.length > 0 ||
    transformerError.length > 0;

  if (!open) {
    return null;
  }

  const sectionHeaderSx = { fontWeight: 600, mb: 1.5, mt: 0 };

  return (
    <DialogGlynt
      open={open}
      closeModal={handleClose}
      width="640px"
      cypressId="dialog-datapool-settings"
      loading={isSaving}
      >
        <DialogTitleGlynt
          title={`Edit Settings — ${dataPool.label}`}
          closeModal={handleClose}
          startIcon="update"
          loading={isSaving}
        />
        <DialogContentGlynt scrollable>
        {isLoading ? (
          <LinearProgress />
        ) : (
          <>
        {/* Data Pool Name + Transformer Tags — same row */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            autoFocus
            variant="outlined"
            size="small"
            id="datapool-label"
            label="Data Pool Name"
            placeholder="Data Pool Name"
            type="text"
            value={labelValue}
            disabled={isSaving}
            error={labelError.length > 0}
            onChange={(e) => setLabelValue(e.target.value)}
            helperText={labelError.length > 0 ? labelError : ''}
            sx={{ flex: 1 }}
            data-cy="input-datapool-label"
          />
          <TextField
            variant="outlined"
            size="small"
            id="glynt-tags"
            label="Transformer Tags"
            placeholder="transformer:<id>"
            type="text"
            value={transformerValue}
            disabled={isSaving}
            error={transformerError.length > 0}
            onChange={handleOnChangeTransformer}
            helperText={transformerError.length > 0 ? transformerError : ''}
            sx={{ flex: 1 }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="status-select-label">Status</InputLabel>
            <Select
              labelId="status-select-label"
              value={status}
              variant="outlined"
              label="Status"
              data-cy="select-datapool-status"
              onChange={(e) => setStatus(e.target.value as DataPoolStatusType)}
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel id="use-select-label">Use</InputLabel>
            <Select
              labelId="use-select-label"
              value={use}
              variant="outlined"
              label="Use"
              data-cy="select-datapool-use"
              onChange={(e) => setUse(e.target.value as DataPoolUseType)}
            >
              <MenuItem value="TEST">Test</MenuItem>
              <MenuItem value="DEMO">Demo</MenuItem>
              <MenuItem value="PRODUCTION">Production</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <FormControl fullWidth size="small">
              <InputLabel id="usage-decimal-places-select-label">Usage Decimals</InputLabel>
              <Select
                labelId="usage-decimal-places-select-label"
                value={usageDecimalPlaces}
                variant="outlined"
                label="Usage Decimals"
                disabled={isSaving}
                data-cy="select-datapool-usage-decimal-places"
                onChange={(e) => setUsageDecimalPlaces(Number(e.target.value))}
              >
                {[2, 3, 4].map((places) => (
                  <MenuItem key={places} value={places}>
                    {places}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Decimal places for usage and demand values. Applies to packages rendered and batches downloaded after the change; re-render an existing package to update it. No effect on pools using a legacy transformer.">
              <IconButton size="small">
                <InfoOutlined fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Client */}
        <Box sx={{ mt: 3 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="client-select-label">Client</InputLabel>
            <Select
              labelId="client-select-label"
              value={clientValue ?? ''}
              variant="outlined"
              label="Client"
              data-cy="select-datapool-client"
              onChange={(e) => {
                const val = e.target.value;
                setClientValue(val === '' ? null : (val as number));
              }}
            >
              <MenuItem value="">None</MenuItem>
              {clientsList.map((c) => (
                <MenuItem key={c.id} value={c.id} data-cy={`client-option-${c.abbr}`}>
                  {c.name} ({c.abbr})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Suggest New Vendors */}
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={suggestNewVendors}
                onChange={(e) => setSuggestNewVendors(e.target.checked)}
                data-cy="checkbox-suggest-new-vendors"
              />
            }
            label="Suggest New Vendors"
          />
          <Tooltip title="Create vendor change tickets when new vendors are discovered for this data pool.">
            <IconButton size="small">
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Validation Datasets */}
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
          {(['extracted', 'transformed', 'schematized'] as ValidationDataset[]).map((dataset) => (
            <FormControlLabel
              key={dataset}
              control={
                <Checkbox
                  checked={validationDatasets.includes(dataset)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setValidationDatasets([...validationDatasets, dataset]);
                    } else {
                      setValidationDatasets(validationDatasets.filter((d) => d !== dataset));
                    }
                  }}
                  data-cy={`checkbox-validation-${dataset}`}
                />
              }
              label={dataset.charAt(0).toUpperCase() + dataset.slice(1)}
            />
          ))}
          <Tooltip title="Select which datasets should be validated for this data pool.">
            <IconButton size="small">
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* MAT Config */}
        <Typography variant="subtitle2" sx={{ ...sectionHeaderSx, mt: 3 }}>
          MAT Config
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Data Relevant From"
              value={dataRelevantFrom}
              onChange={(date) => setDataRelevantFrom(date)}
              disabled={isSaving}
              slotProps={{
                textField: {
                  variant: 'outlined',
                  size: 'small',
                  sx: { width: 200 },
                  inputProps: { 'data-cy': 'datepicker-data-relevant-from' },
                },
                field: {
                  clearable: true,
                  onClear: () => setDataRelevantFrom(null),
                },
              }}
            />
          </LocalizationProvider>
          <Tooltip title="When set, MAT discovery results are filtered to only include records on or after this date.">
            <IconButton size="small">
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Router Prefix + Priority + OCR Engine — same row */}
        <Typography variant="subtitle2" sx={{ ...sectionHeaderSx, mt: 3 }}>
          Router & Priority
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <TextField
            variant="outlined"
            size="small"
            data-cy={`input-router-prefix-${dataPool.label}`}
            id="router-tags"
            label="Router Prefix"
            placeholder="sample_prefix/123"
            disabled={isLoadingRouteConfig || isFetchingRouteConfig || isSaving}
            value={
              isLoadingRouteConfig || isFetchingRouteConfig ? 'Loading...' : routerPrefixValue
            }
            error={routerPrefixError.length > 0}
            type="text"
            onChange={handleOnChangeRouterPrefix}
            helperText={
              routerPrefixError.length > 0
                ? routerPrefixError
                : 'Route prefix for this data pool'
            }
            sx={{ flex: 2 }}
          />
          <FormControl variant="outlined" size="small" sx={{ flex: 1 }}>
            <InputLabel id="priority-select-label">Priority</InputLabel>
            <Select
              labelId="priority-select-label"
              value={priority}
              variant="outlined"
              label="Priority"
              data-cy="select-datapool-priority"
              onChange={(e) => setPriority(e.target.value as DataPoolPriorityType)}
            >
              <MenuItem value="Normal">Normal</MenuItem>
              <MenuItem value="High">High</MenuItem>
            </Select>
          </FormControl>
          <FormControl variant="outlined" size="small" sx={{ flex: 1 }}>
            <InputLabel id="ocr-engine-select-label">OCR Engine</InputLabel>
            <Select
              labelId="ocr-engine-select-label"
              value={version ?? ''}
              variant="outlined"
              label="OCR Engine"
              data-cy="ocr-selector"
              onChange={(e) => setVersion(e.target.value as string)}
            >
              {data?.active.map((tv) => (
                <MenuItem key={tv} value={tv} data-cy="ocr-selector-item-current">
                  {mapTokenizerVersion(tv)}
                </MenuItem>
              ))}
              {isStaff &&
                data?.experimental.map((tv) => (
                  <MenuItem key={tv} value={tv} data-cy="ocr-selector-item">
                    {mapTokenizerVersion(tv)} (beta)
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>
          </>
        )}
      </DialogContentGlynt>
      <DialogActionsGlynt
        handleSubmit={handleSubmit}
        closeModal={handleClose}
        cancelCypressId="btn-edit-settings-cancel"
        submitCypressId="btn-edit-settings-submit"
        clearCypressId="btn-edit-settings-clear"
        submitLoading={isSaving}
        submitDisabled={isLoading || isError || isSubmitDisabled}
      />
    </DialogGlynt>
  );
}

export default GlyntTagsEditDialog;
